/**
 * Iter103: Zentrale Schweizer Datums-Formatierung.
 *
 * Schweiz-Standard:
 *   - Datum: DD.MM.YYYY  (z.B. 27.05.2026)
 *   - Datum+Zeit: DD.MM.YYYY HH:mm  (z.B. 27.05.2026 21:15)
 *   - Datum lang: DD. Monat YYYY  (z.B. 27. Mai 2026)  — nur für Briefe/Quittungen
 *
 * Verwendet `toLocaleDateString("de-CH", {2-digit})` für konsistente Darstellung.
 * Akzeptiert: ISO-String (YYYY-MM-DD oder voll-ISO), Date-Objekt, null/undefined.
 */

const SWISS_DATE_OPTS = { year: "numeric", month: "2-digit", day: "2-digit" };
const SWISS_TIME_OPTS = { hour: "2-digit", minute: "2-digit" };
const SWISS_LONG_OPTS = { year: "numeric", month: "long", day: "2-digit" };

/** Akzeptiert ISO-String, Date oder null. Returns "" für leere Werte, ansonsten DD.MM.YYYY. */
export function formatDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-CH", SWISS_DATE_OPTS);
}

/** DD.MM.YYYY, HH:mm — z.B. für Audit-Logs, created_at, updated_at. */
export function formatDateTime(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const datePart = d.toLocaleDateString("de-CH", SWISS_DATE_OPTS);
  const timePart = d.toLocaleTimeString("de-CH", SWISS_TIME_OPTS);
  return `${datePart}, ${timePart}`;
}

/** "27. Mai 2026" — für Briefe / PDF-Headlines. */
export function formatDateLong(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-CH", SWISS_LONG_OPTS);
}

/** Konvertiert Date oder ISO zu YYYY-MM-DD (für `<input type="date">` und Backend). */
export function toIsoDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/** Relativ-Zeit ("vor 5 Min", "gestern") — falls in den letzten 7 Tagen, sonst Datum. */
export function formatRelative(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "gerade eben";
  if (diffMin < 60) return `vor ${diffMin} Min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "gestern";
  if (diffD < 7) return `vor ${diffD} Tagen`;
  return formatDate(d);
}
